import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip, message } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { dateToStringWT, getFullName } from '@/utils';
import { DownloadSvg, PlusSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import { useDownloadChildrenByStatusMutation } from '@/services/child-api';
import { selectorPermissions } from '@/stores/auth';

// const { Text } = Typography;

const Wrapper = styled.div`
  padding: 0 24px 24px 24px;
  background-color: var(--colorBgContainer);
  border-radius: var(--borderRadius);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-block: 16px;
  padding-right: 8px;

  .title {
    font-size: 16px;
    font-weight: 500;
    margin-right: auto;
  }
`;

const columns = [
  {
    title: 'ID',
    dataIndex: 'childCode',
    render: (value, record) => <Link to={`/child/${record.childId}`}>{value}</Link>,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    render: (value) => getFullName(value),
  },
  {
    title: 'County',
    dataIndex: 'county',
    render: (value, record) => (
      <>
        <div>{value.name}</div>
        <i>{record.region.name}</i>
      </>
    ),
  },
  {
    title: 'DOB',
    dataIndex: 'birthInfo',
    render: (value) => {
      const d = dayjs(value.date);
      return (
        <>
          <div>{dateToStringWT(d)}</div>({value.age} years)
        </>
      );
    },
  },
  {
    title: 'Adoption Liaison',
    dataIndex: 'adoptionLiaison',
    render: (value) => (
      <>
        <div>{getFullName(value.name)}</div>
        <a href={`mailto:${value.emailAddress}`}>{value.emailAddress}</a>
      </>
    ),
  },
  {
    title: 'Caseworker',
    dataIndex: 'caseworker',
    render: (value) => (
      <>
        <div>{getFullName(value.name)}</div>
        <a href={`mailto:${value.emailAddress}`}>{value.emailAddress}</a>
      </>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (value, record) => (
      <>
        <div>{value.name}</div>
        {!!record.remainingTasks?.length && (
          <>
            <i>Remaining Tasks</i>
            <ul style={{ margin: 0 }}>
              {record.remainingTasks.map((task) => (
                <li key={task.name}>{task.name}</li>
              ))}
            </ul>
          </>
        )}
      </>
    ),
  },
];

const QuickViewResults = ({ status, reportType, data, title, loading }) => {
  const permissions = useSelector(selectorPermissions);
  const [downloadByStatus, { isDownloadingByStatus }] = useDownloadChildrenByStatusMutation();

  const downloadToExcel = async () => {
    try {
      let resData;
      if (status) resData = await downloadByStatus({ statusKey: status, reportType }).unwrap();

      if (resData) {
        // Convert Base64 string to a Blob
        const byteCharacters = atob(resData.fileBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const fileBlob = new Blob([byteArray], { type: resData.contentType });

        // Create a download link and click it programmatically
        const url = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', resData.fileName);
        document.body.appendChild(link);
        link.click();

        // Clean up by revoking the Object URL and removing the link element
        window.URL.revokeObjectURL(url);
        link.parentNode.removeChild(link);
        message.success('Completed!');
      }
    } catch (error) {
      console.error('Download error:', error);
      // Handle error appropriately
    }
  };

  return (
    <Wrapper id="quick-view-results">
      <Toolbar>
        <div className="title">{title}</div>
        {permissions.writeParent && (
          <Button type="primary" icon={<PlusSvg />} to="/parent/new">
            Add
          </Button>
        )}
        <Tooltip title="Download Results">
          <Button
            type="link"
            icon={<DownloadSvg fontSize="18" />}
            disabled={!data?.count}
            onClick={downloadToExcel}
            loading={isDownloadingByStatus}
          />
        </Tooltip>
      </Toolbar>

      <Table columns={columns} dataSource={data?.data} rowKey="childId" loading={loading} pageInfo={data} />
    </Wrapper>
  );
};

export default memo(QuickViewResults);
